import { Suspense } from 'react';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useIsMobileScreen } from '@/hooks/useMedia';
import { ErrorBoundary } from '@sentry/react';
import { Footer } from '@/components/Footer';
import { PageMeta } from './components/PageMeta';
import Loader from '@assets/images/portfolio/loader.svg';
import * as Section from './components/Sections';
import { Header } from './components/Header';
import DocumentHeader from '@/components/DocumentHeader';

const PageMobile = () => (
  <div>
    <div className="p-4 text-solid">
      <Header />
      <main className="flex flex-col gap-3">
        <Section.Chart />
        <Section.Details />
        <Section.Narratives hasSlider={false} />
        <Section.Performance />
        <Section.News />
        <Section.Insights />
        <Section.Competitors />
      </main>
    </div>
    <Footer />
  </div>
);

const PageDesktop = () => (
  <div className="mx-auto w-full max-w-screen-xl px-6 pb-4 text-solid">
    <Header />
    <div className="grid grid-cols-[65.5%_minmax(0,1fr)] gap-6">
      <main className="flex flex-col gap-3">
        <Section.Chart />
        <Section.Narratives />
        <Section.News />
        <Section.Competitors />
      </main>
      <aside>
        <div className="scrollbar sticky top-20 flex flex-col gap-3 overflow-y-auto overflow-x-hidden [@media(min-height:1000px)]:max-h-[calc(100vh-80px)] [@media(min-height:1000px)]:pr-3">
          <Section.Details />
          <Section.Performance />
          <Section.Insights />
        </div>
      </aside>
    </div>
    <Footer />
  </div>
);

const PageSwitcher = () => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  return (
    <>
      <PageMeta />
      {isMobileScreen ? <PageMobile /> : <PageDesktop />}
    </>
  );
};

const MessageLayout = ({ children }: { children: React.ReactNode }) => (
  <div className="absolute inset-0 grid h-2/5 place-content-center py-x8 text-base">
    {children}
  </div>
);

export const StockPage = () => {
  return (
    <Suspense
      fallback={
        <MessageLayout>
          <Loader className="size-x4 animate-spin" />
        </MessageLayout>
      }
    >
      <ErrorBoundary
        fallback={(e) => {
          console.error({ e });
          return (
            <MessageLayout>
              {e.error instanceof Error &&
              e.error.message.startsWith('Could not find company') ? (
                <div>
                  <DocumentHeader
                    title="Page Not Found"
                    allowCrawlers={false}
                  />
                  <span className="text-balance text-solid">
                    Sorry, this page was not found.
                  </span>
                </div>
              ) : (
                <div>
                  <DocumentHeader title="Something went wrong" />
                  <span className="text-bad">
                    Something went wrong, please refresh the page.
                  </span>
                </div>
              )}
            </MessageLayout>
          );
        }}
      >
        <PageSwitcher />
      </ErrorBoundary>
    </Suspense>
  );
};
